import { Simulation } from '@/hooks/useSimulation';
import { Beneficiary } from '@/types/beneficiary';
import { CurrencyCodeType } from '@/types/currency';
import { GenericAction } from '@/utility/reducers';

export type InternalTransferDirection = 'main_to_customer' | 'customer_to_main';

export interface InternalTransferFlowState {
  readonly direction: InternalTransferDirection | null;
  readonly customerBeneficiaryId: string;
  readonly currency: CurrencyCodeType;
  readonly fromAmount: number | null;
  readonly toAmount: number | null;
  readonly beneficiaries: readonly Beneficiary[];
  readonly mainBeneficiaryId: string;
}

export const initialState: InternalTransferFlowState = {
  direction: null,
  customerBeneficiaryId: '',
  currency: CurrencyCodeType.UNSET,
  fromAmount: null,
  toAmount: null,
  beneficiaries: [],
  mainBeneficiaryId: '',
};

export enum InternalTransferActionType {
  directionChanged = 'directionChanged',
  customerBeneficiaryChanged = 'customerBeneficiaryChanged',
  currencyChanged = 'currencyChanged',
  fromAmountChanged = 'fromAmountChanged',
  toAmountChanged = 'toAmountChanged',
  simulationCreated = 'simulationCreated',
  beneficiariesLoaded = 'beneficiariesLoaded',
  mainBeneficiarySet = 'mainBeneficiarySet',
  reset = 'reset',
}

export type DirectionChangedAction = GenericAction<
  InternalTransferActionType.directionChanged,
  InternalTransferDirection
>;

export type CustomerBeneficiaryChangedAction = GenericAction<
  InternalTransferActionType.customerBeneficiaryChanged,
  string
>;

export type CurrencyChangedAction = GenericAction<
  InternalTransferActionType.currencyChanged,
  CurrencyCodeType
>;

export type FromAmountChangedAction = GenericAction<
  InternalTransferActionType.fromAmountChanged,
  number | null
>;

export type ToAmountChangedAction = GenericAction<
  InternalTransferActionType.toAmountChanged,
  number | null
>;

export type SimulationCreatedAction = GenericAction<
  InternalTransferActionType.simulationCreated,
  Simulation
>;

export type BeneficiariesLoadedAction = GenericAction<
  InternalTransferActionType.beneficiariesLoaded,
  readonly Beneficiary[]
>;

export type MainBeneficiarySetAction = GenericAction<
  InternalTransferActionType.mainBeneficiarySet,
  string
>;

export type ResetAction = GenericAction<InternalTransferActionType.reset, void>;

export type InternalTransferAction =
  | DirectionChangedAction
  | CustomerBeneficiaryChangedAction
  | CurrencyChangedAction
  | FromAmountChangedAction
  | ToAmountChangedAction
  | SimulationCreatedAction
  | BeneficiariesLoadedAction
  | MainBeneficiarySetAction
  | ResetAction;

export const applySimulation = (
  state: InternalTransferFlowState,
  simulation: Simulation,
): InternalTransferFlowState => {
  const { from, to } = simulation;

  return {
    ...state,
    fromAmount: state.fromAmount ?? (from.value !== null ? Number(from.value) : null),
    toAmount: state.toAmount ?? (to.value !== null ? Number(to.value) : null),
  };
};

export const internalTransferReducer = (
  state: InternalTransferFlowState,
  action: InternalTransferAction,
): InternalTransferFlowState => {
  switch (action.type) {
    case InternalTransferActionType.directionChanged:
      if (state.direction === action.payload) {
        return state;
      }
      return {
        ...state,
        direction: action.payload,
        currency: CurrencyCodeType.UNSET,
        fromAmount: null,
        toAmount: null,
      };
    case InternalTransferActionType.customerBeneficiaryChanged:
      if (state.customerBeneficiaryId === action.payload) {
        return state;
      }
      return {
        ...state,
        customerBeneficiaryId: action.payload,
        currency: CurrencyCodeType.UNSET,
        fromAmount: null,
        toAmount: null,
      };
    case InternalTransferActionType.currencyChanged:
      if (state.currency === action.payload) {
        return state;
      }
      return {
        ...state,
        currency: action.payload,
        fromAmount: null,
        toAmount: null,
      };
    case InternalTransferActionType.fromAmountChanged:
      return { ...state, fromAmount: action.payload, toAmount: null };
    case InternalTransferActionType.toAmountChanged:
      return { ...state, toAmount: action.payload, fromAmount: null };
    case InternalTransferActionType.simulationCreated:
      return applySimulation(state, action.payload);
    case InternalTransferActionType.beneficiariesLoaded:
      return { ...state, beneficiaries: action.payload };
    case InternalTransferActionType.mainBeneficiarySet:
      return { ...state, mainBeneficiaryId: action.payload };
    case InternalTransferActionType.reset:
      return initialState;
    default:
      return state;
  }
};

export const directionChanged = (payload: InternalTransferDirection): DirectionChangedAction => ({
  type: InternalTransferActionType.directionChanged,
  payload,
});

export const customerBeneficiaryChanged = (payload: string): CustomerBeneficiaryChangedAction => ({
  type: InternalTransferActionType.customerBeneficiaryChanged,
  payload,
});

export const currencyChanged = (payload: CurrencyCodeType): CurrencyChangedAction => ({
  type: InternalTransferActionType.currencyChanged,
  payload,
});

export const fromAmountChanged = (payload: number | null): FromAmountChangedAction => ({
  type: InternalTransferActionType.fromAmountChanged,
  payload,
});

export const toAmountChanged = (payload: number | null): ToAmountChangedAction => ({
  type: InternalTransferActionType.toAmountChanged,
  payload,
});

export const simulationCreated = (payload: Simulation): SimulationCreatedAction => ({
  type: InternalTransferActionType.simulationCreated,
  payload,
});

export const beneficiariesLoaded = (
  payload: readonly Beneficiary[],
): BeneficiariesLoadedAction => ({
  type: InternalTransferActionType.beneficiariesLoaded,
  payload,
});

export const mainBeneficiarySet = (payload: string): MainBeneficiarySetAction => ({
  type: InternalTransferActionType.mainBeneficiarySet,
  payload,
});

export const reset = (): ResetAction => ({
  type: InternalTransferActionType.reset,
  payload: undefined,
});

export const getSourceBeneficiaryId = (state: InternalTransferFlowState): string => {
  if (state.direction === 'main_to_customer') {
    return state.mainBeneficiaryId;
  }
  if (state.direction === 'customer_to_main') {
    return state.customerBeneficiaryId;
  }
  return '';
};

export const getDestinationBeneficiaryId = (state: InternalTransferFlowState): string => {
  if (state.direction === 'main_to_customer') {
    return state.customerBeneficiaryId;
  }
  if (state.direction === 'customer_to_main') {
    return state.mainBeneficiaryId;
  }
  return '';
};
